import { useTranslation } from "react-i18next";
import { cn } from "@/zapi-frontend/shared/lib/utils";

type TChipTone = "positive" | "caution" | "negative" | "neutral";

const TONE_WORDS: Record<Exclude<TChipTone, "neutral">, string[]> = {
  negative: ["high", "not now", "no", "fail", "failed", "rejected", "weak", "negative", "critical", "blocked"],
  caution: ["medium", "moderate", "weakened", "partial", "partially", "conditional", "watch", "unclear", "at risk"],
  positive: ["low", "recommended", "yes", "pass", "passed", "strong", "positive", "confirmed", "on track"]
};

// Japanese has no spaces between words, so the leading phrase has to match one of these exactly.
const TONE_WORDS_JA: Record<Exclude<TChipTone, "neutral">, string[]> = {
  negative: ["高", "高い", "見送り", "いいえ", "不合格", "却下", "弱い", "ネガティブ", "重大", "ブロック中"],
  caution: ["中", "中程度", "弱まった", "部分的", "条件付き", "要注意", "不明確", "リスクあり"],
  positive: ["低", "低い", "推奨", "はい", "合格", "強い", "ポジティブ", "確認済み", "順調"]
};

// The generation run writes these ratings, sometimes in English inside a Japanese memo, so a cell
// that is exactly one of them is shown in the page's language. Longer phrases are left as written.
const LABEL_KEYS = {
  high: "high",
  medium: "medium",
  low: "low",
  "not now": "notNow",
  no: "no",
  yes: "yes",
  fail: "fail",
  failed: "failed",
  pass: "pass",
  passed: "passed",
  rejected: "rejected",
  recommended: "recommended",
  weak: "weak",
  weakened: "weakened",
  strong: "strong",
  negative: "negative",
  positive: "positive",
  critical: "critical",
  blocked: "blocked",
  moderate: "moderate",
  partial: "partial",
  partially: "partially",
  conditional: "conditional",
  watch: "watch",
  unclear: "unclear",
  "at risk": "atRisk",
  confirmed: "confirmed",
  "on track": "onTrack"
} as const;

const TONE_CLASSES: Record<TChipTone, string> = {
  positive: "border-innoveraGreen-700 bg-innoveraGreen-50 text-innoveraGreen-700",
  caution: "border-innoveraYellow-500 bg-innoveraYellow-50 text-innoveraYellow-600",
  negative: "border-innoveraRed-500 bg-innoveraRed-50 text-innoveraRed-700",
  neutral: "border-innoveraZinc-150 bg-innoveraZinc-100 text-innoveraZinc-900"
};

// The verdict's opening words decide the colour, so "Not now; it needs equity" reads as negative.
export const chipTone = (value: string): TChipTone => {
  const words = value
    .toLowerCase()
    .replace(/[^a-z\s]/g, " ")
    .trim()
    .split(/\s+/);
  const openings = [words[0], words.slice(0, 2).join(" ")];
  for (const tone of ["negative", "caution", "positive"] as const) {
    if (TONE_WORDS[tone].some((word) => openings.includes(word))) return tone;
  }
  const leading = value.trim().split(/[\s;；、,，:：()（）。]/)[0];
  for (const tone of ["negative", "caution", "positive"] as const) {
    if (TONE_WORDS_JA[tone].includes(leading)) return tone;
  }
  return "neutral";
};

export function RecordChip({ value }: { value: string }) {
  const { t } = useTranslation("projectHome");
  const labelKey = LABEL_KEYS[value.trim().toLowerCase() as keyof typeof LABEL_KEYS];
  const label = labelKey ? t(`recordChip.${labelKey}`) : value.charAt(0).toUpperCase() + value.slice(1);

  return (
    <span
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-1 text-[12px] font-medium leading-4",
        TONE_CLASSES[chipTone(value)]
      )}
    >
      {label}
    </span>
  );
}
